import { computed, nextTick, onUnmounted, ref, shallowRef, watch } from 'vue'
import { useEventListener } from '@vueuse/core'

const VIEWPORT_PAD = 8
const GAP = 6

const FLIP_ORDER = {
  top: ['top', 'bottom', 'right', 'left'],
  bottom: ['bottom', 'top', 'right', 'left'],
  right: ['right', 'left', 'top', 'bottom'],
  left: ['left', 'right', 'top', 'bottom'],
}

function fitsInViewport(top, left, width, height) {
  return (
    top >= VIEWPORT_PAD
    && left >= VIEWPORT_PAD
    && top + height <= window.innerHeight - VIEWPORT_PAD
    && left + width <= window.innerWidth - VIEWPORT_PAD
  )
}

function computeCoords(triggerRect, width, height, preferredPlacement) {
  const order = FLIP_ORDER[preferredPlacement] ?? FLIP_ORDER.top
  const centerX = triggerRect.left + triggerRect.width / 2
  const centerY = triggerRect.top + triggerRect.height / 2

  const variants = {
    top: {
      placement: 'top',
      top: triggerRect.top - GAP - height,
      left: centerX - width / 2,
    },
    bottom: {
      placement: 'bottom',
      top: triggerRect.bottom + GAP,
      left: centerX - width / 2,
    },
    right: {
      placement: 'right',
      top: centerY - height / 2,
      left: triggerRect.right + GAP,
    },
    left: {
      placement: 'left',
      top: centerY - height / 2,
      left: triggerRect.left - GAP - width,
    },
  }

  let chosen = variants[order[0]]
  for (const key of order) {
    const candidate = variants[key]
    if (fitsInViewport(candidate.top, candidate.left, width, height)) {
      chosen = candidate
      break
    }
  }

  const vw = window.innerWidth
  const vh = window.innerHeight
  const left = Math.max(VIEWPORT_PAD, Math.min(chosen.left, vw - width - VIEWPORT_PAD))
  const top = Math.max(VIEWPORT_PAD, Math.min(chosen.top, vh - height - VIEWPORT_PAD))

  return { top, left, placement: chosen.placement }
}

export { computeCoords }

/**
 * 将 tooltip Teleport 到 body，按视口翻转并 clamp。
 * 触发器为组件挂载处的父元素（按钮 / 链接等）。
 */
export function useViewportTooltip(preferredPlacement = 'top', options = {}) {
  const clickable = Boolean(options.clickable)
  const visible = ref(false)
  const floatingRef = ref(null)
  const coords = shallowRef({ top: 0, left: 0 })
  const resolvedPlacement = ref('top')
  const triggerRef = shallowRef(null)
  const hovered = ref(false)
  const focused = ref(false)
  const pinned = ref(false)
  let suppressedUntilPointerExit = false

  const coordsStyle = computed(() => ({
    top: `${coords.value.top}px`,
    left: `${coords.value.left}px`,
  }))

  function resolveTriggerEl(instance) {
    const anchor = instance?.vnode?.el
    if (!anchor) return null
    if (anchor.nodeType === Node.COMMENT_NODE) return anchor.parentElement
    if (anchor.nodeType === Node.ELEMENT_NODE) return anchor.parentElement
    return null
  }

  function updatePosition() {
    const trigger = triggerRef.value
    const tip = floatingRef.value
    if (!trigger || !tip || typeof window === 'undefined') return

    const triggerRect = trigger.getBoundingClientRect()
    const { top, left, placement } = computeCoords(
      triggerRect,
      tip.offsetWidth,
      tip.offsetHeight,
      preferredPlacement,
    )
    coords.value = { top, left }
    resolvedPlacement.value = placement
  }

  async function show() {
    if (visible.value) {
      updatePosition()
      return
    }
    visible.value = true
    await nextTick()
    updatePosition()
  }

  function hide() {
    visible.value = false
  }

  function syncVisibility() {
    if (pinned.value || (!suppressedUntilPointerExit && (hovered.value || focused.value))) {
      show()
      return
    }
    hide()
  }

  function onMouseEnter() {
    hovered.value = true
    suppressedUntilPointerExit = false
    syncVisibility()
  }

  function onMouseLeave() {
    hovered.value = false
    if (!clickable) {
      hide()
      return
    }
    suppressedUntilPointerExit = false
    syncVisibility()
  }

  function onFocusIn() {
    focused.value = true
    suppressedUntilPointerExit = false
    syncVisibility()
  }

  function onFocusOut(event) {
    const trigger = triggerRef.value
    if (!trigger) return
    const next = event.relatedTarget
    if (next && trigger.contains(next)) return
    focused.value = false
    if (!clickable) {
      hide()
      return
    }
    pinned.value = false
    syncVisibility()
  }

  function onClick() {
    if (!clickable) return
    const trigger = triggerRef.value

    if (pinned.value) {
      pinned.value = false
      focused.value = false
      suppressedUntilPointerExit = true
      trigger?.blur?.()
      hide()
      return
    }

    pinned.value = true
    suppressedUntilPointerExit = false
    show()
  }

  function onPointerDownOutside(event) {
    const trigger = triggerRef.value
    if (!clickable || !visible.value || !trigger) return
    if (event.target instanceof Node && trigger.contains(event.target)) return

    pinned.value = false
    focused.value = false
    suppressedUntilPointerExit = true
    trigger.blur?.()
    hide()
  }

  function onDocumentKeydown(event) {
    if (!clickable || !visible.value || event.key !== 'Escape') return
    pinned.value = false
    focused.value = false
    suppressedUntilPointerExit = true
    triggerRef.value?.blur?.()
    hide()
  }

  function bindTrigger(instance) {
    const trigger = resolveTriggerEl(instance)
    triggerRef.value = trigger
    if (!trigger) return

    trigger.addEventListener('mouseenter', onMouseEnter)
    trigger.addEventListener('mouseleave', onMouseLeave)
    trigger.addEventListener('focusin', onFocusIn)
    trigger.addEventListener('focusout', onFocusOut)
    if (clickable) {
      trigger.addEventListener('click', onClick)
      document.addEventListener('pointerdown', onPointerDownOutside, true)
      document.addEventListener('keydown', onDocumentKeydown)
    }

    return () => {
      trigger.removeEventListener('mouseenter', onMouseEnter)
      trigger.removeEventListener('mouseleave', onMouseLeave)
      trigger.removeEventListener('focusin', onFocusIn)
      trigger.removeEventListener('focusout', onFocusOut)
      if (clickable) {
        trigger.removeEventListener('click', onClick)
        document.removeEventListener('pointerdown', onPointerDownOutside, true)
        document.removeEventListener('keydown', onDocumentKeydown)
      }
    }
  }

  let unbindScroll = null
  let unbindResize = null

  function startViewportListeners() {
    stopViewportListeners()
    unbindScroll = useEventListener(window, 'scroll', updatePosition, {
      capture: true,
      passive: true,
    })
    unbindResize = useEventListener(window, 'resize', updatePosition, { passive: true })
  }

  function stopViewportListeners() {
    unbindScroll?.()
    unbindResize?.()
    unbindScroll = null
    unbindResize = null
  }

  watch(visible, (isVisible) => {
    if (isVisible) {
      startViewportListeners()
      nextTick(updatePosition)
    } else {
      stopViewportListeners()
    }
  })

  onUnmounted(stopViewportListeners)

  return {
    visible,
    floatingRef,
    coordsStyle,
    resolvedPlacement,
    show,
    hide,
    updatePosition,
    bindTrigger,
  }
}
